import { useEffect } from 'react'
import { Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Checkbox } from 'primereact/checkbox'
import FormDialog from '../../../components/ui/FormDialog'
import FormDateField from '../../../components/ui/form/FormDateField'
import FormTextField from '../../../components/ui/form/FormTextField'
import { useZodForm } from '../../../lib/forms/useZodForm'
import { holidayDayFormSchema, type HolidayDayFormValues } from './holidayDayFormSchema'
import type { HolidayDay, HolidayDayUpsert } from '../types'

interface Props {
  visible: boolean
  initial: HolidayDay | null
  submitting: boolean
  dateError?: string | null
  onClearDateError: () => void
  onSubmit: (payload: HolidayDayUpsert) => void
  onHide: () => void
}

/**
 * Tatil Günü ekle/düzenle formu (react-hook-form + zod): tarih · TR açıklama · EN açıklama · yarım gün.
 *
 * <p>Aynı güne ikinci kayıt sunucudan `DUPLICATE_NAME` ile döner ve **tarih alanının** hatası olarak
 * gösterilir (toast değil): kullanıcı hangi alanı düzelteceğini görmeli.
 *
 * <p>Yarım gün kutusu `Controller` ile bağlanır — projede RHF'ye bağlı hazır bir onay kutusu bileşeni
 * yok ve tek kullanım için paylaşılan bileşen açmak erken olurdu; ikinci bir ekran ihtiyaç duyarsa
 * `ui/form/` altına taşınmalı.
 */
export default function HolidayDayFormDialog({
  visible,
  initial,
  submitting,
  dateError,
  onClearDateError,
  onSubmit,
  onHide,
}: Readonly<Props>) {
  const { t } = useTranslation()
  const { control, handleSubmit, reset, setError } = useZodForm(holidayDayFormSchema, {
    defaultValues: { holidayDate: '', descriptionTr: '', descriptionEn: '', halfDay: false },
  })

  useEffect(() => {
    if (visible)
      reset({
        holidayDate: initial?.holidayDate ?? '',
        descriptionTr: initial?.descriptionTr ?? '',
        descriptionEn: initial?.descriptionEn ?? '',
        halfDay: initial?.halfDay ?? false,
      })
  }, [visible, initial, reset])

  useEffect(() => {
    if (dateError) {
      setError('holidayDate', { type: 'server', message: dateError })
      onClearDateError()
    }
  }, [dateError, setError, onClearDateError])

  const onValid = (v: HolidayDayFormValues) =>
    onSubmit({
      holidayDate: v.holidayDate,
      descriptionTr: v.descriptionTr,
      descriptionEn: v.descriptionEn ? v.descriptionEn : null,
      halfDay: v.halfDay,
    })

  return (
    <FormDialog
      visible={visible}
      header={initial ? t('holidayDays.form.editTitle') : t('holidayDays.form.newTitle')}
      submitLabel={t('holidayDays.form.save')}
      submitting={submitting}
      submitDisabled={submitting}
      onSubmit={handleSubmit(onValid)}
      onHide={onHide}
    >
      <div className="admin-form">
        <FormDateField
          control={control}
          name="holidayDate"
          label={t('holidayDays.columns.date')}
          required
        />
        <FormTextField
          control={control}
          name="descriptionTr"
          label={t('holidayDays.form.descriptionTr')}
          required
        />
        <FormTextField
          control={control}
          name="descriptionEn"
          label={t('holidayDays.form.descriptionEn')}
        />
        <Controller
          control={control}
          name="halfDay"
          render={({ field }) => (
            <span className="admin-form-check">
              <Checkbox
                inputId="holiday-half-day"
                checked={field.value}
                onChange={(e) => field.onChange(Boolean(e.checked))}
              />
              <label htmlFor="holiday-half-day">{t('holidayDays.columns.halfDay')}</label>
            </span>
          )}
        />
      </div>
    </FormDialog>
  )
}
